import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, ChevronDown, Lock, ShieldCheck, LogOut } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import * as Icons from 'lucide-react';
import Logo from './Logo';
import IntelligenceBar from './IntelligenceBar';
import { useT } from '../i18n';
import { TextSizeInline } from './TextSizeControl';
import { useAuthUser, logoutSession, clearAuthCache } from '../lib/auth';
import { Button } from './ui/button';

/* -------------------------------------------------------------------- */
/*  Mega-menu data sources                                              */
/* -------------------------------------------------------------------- */

function buildServiceGroups(lang) {
  const isEs = lang === 'es';
  return [
    {
      title: isEs ? 'Tecnología para Restaurantes' : 'Restaurant Technology',
      href: '/restaurant-technology',
      items: [
        { icon: 'MonitorSmartphone', label: isEs ? 'IT y Sistemas POS' : 'IT & POS Systems', desc: isEs ? 'Conecta cada sistema del restaurante.' : 'Keep every restaurant system connected.', href: '/it-pos-systems' },
        { icon: 'Settings', label: isEs ? 'Gestión de POS' : 'POS Management', desc: isEs ? 'Menús, modificadores y plataformas.' : 'Menus, modifiers & platform management.', href: '/services/pos-management' },
        { icon: 'Plug', label: isEs ? 'Integraciones de POS' : 'POS Integrations', desc: isEs ? 'Conecta tu POS con todo el ecosistema.' : 'Connect your POS with the full ecosystem.', href: '/services/pos-integrations' },
        { icon: 'ShoppingBag', label: isEs ? 'Pedidos Online' : 'Online Ordering', desc: isEs ? 'Pedidos directos e integraciones de delivery.' : 'Direct ordering & delivery integrations.', href: '/services/online-ordering' },
      ],
    },
    {
      title: isEs ? 'Presencia Digital' : 'Digital Presence',
      href: '/digital-presence',
      items: [
        { icon: 'Sparkles', label: isEs ? 'Branding Digital' : 'Digital Branding', desc: isEs ? 'Construye una marca memorable.' : 'Build a brand guests remember.', href: '/digital-branding' },
        { icon: 'Globe', label: isEs ? 'Sitios Web' : 'Restaurant Websites', desc: isEs ? 'Webs de alta conversión.' : 'High-converting restaurant websites.', href: '/services/restaurant-websites' },
        { icon: 'Search', label: 'Local SEO', desc: isEs ? 'Aparece en búsquedas locales.' : 'Rank where local guests search.', href: '/services/local-seo' },
        { icon: 'MapPin', label: isEs ? 'Perfil de Google' : 'Google Business Profile', desc: isEs ? 'Aparece donde los clientes buscan.' : 'Appear where customers search.', href: '/services/google-business-profile' },
        { icon: 'Star', label: isEs ? 'Reputación Online' : 'Online Reputation', desc: isEs ? 'Gestión de reseñas y reputación.' : 'Reviews & reputation management.', href: '/services/online-reputation' },
      ],
    },
    {
      title: isEs ? 'Crecimiento del Restaurante' : 'Restaurant Growth',
      href: '/restaurant-growth',
      items: [
        { icon: 'TrendingUp', label: isEs ? 'Crecimiento de Ingresos' : 'Revenue Growth', desc: isEs ? 'Aumenta ventas y rentabilidad.' : 'Increase sales and profitability.', href: '/restaurant-revenue-growth' },
        { icon: 'Bike', label: isEs ? 'Optimización de Delivery' : 'Delivery Optimization', desc: isEs ? 'Uber Eats, DoorDash y Grubhub.' : 'Uber Eats, DoorDash & Grubhub performance.', href: '/services/delivery-optimization' },
        { icon: 'Utensils', label: isEs ? 'Crecimiento de Catering' : 'Catering Growth', desc: isEs ? 'Catering corporativo y pedidos grandes.' : 'Corporate catering & large orders.', href: '/services/catering-growth' },
        { icon: 'Mail', label: isEs ? 'Marketing al Cliente' : 'Customer Marketing', desc: isEs ? 'Convierte clientes en habituales.' : 'Turn guests into loyal customers.', href: '/services/customer-marketing' },
      ],
    },
    {
      title: isEs ? 'Inteligencia del Restaurante' : 'Restaurant Intelligence',
      href: '/restaurant-intelligence',
      items: [
        { icon: 'PieChart', label: isEs ? 'Datos y Analítica' : 'Data & Analytics', desc: isEs ? 'KPIs y rendimiento del restaurante.' : 'Restaurant KPIs & performance.', href: '/services/data-analytics' },
        { icon: 'LayoutDashboard', label: 'Dashboards', desc: isEs ? 'Visibilidad de negocio en tiempo real.' : 'Real-time business visibility.', href: '/services/dashboards' },
        { icon: 'FileBarChart', label: isEs ? 'Reportes de Rendimiento' : 'Performance Reporting', desc: isEs ? 'Reportes ejecutivos y recomendaciones.' : 'Executive reporting & recommendations.', href: '/services/performance-reporting' },
        { icon: 'Lightbulb', label: isEs ? 'Business Intelligence' : 'Business Intelligence', desc: isEs ? 'Decisiones basadas en datos.' : 'Data-driven decisions, every shift.', href: '/services/business-intelligence' },
      ],
    },
  ];
}

function buildAiGroups(lang) {
  const isEs = lang === 'es';
  return [
    {
      title: isEs ? 'Plataformas de IA' : 'AI Platforms',
      href: '/ai-solutions',
      items: [
        { icon: 'Search', label: 'Index AI', desc: isEs ? 'Visibilidad para Google, Maps y AI Search.' : 'AI visibility for Google, Maps & AI Search.', href: '/index-ai' },
        { icon: 'Users', label: isEs ? 'Agentes de IA' : 'AI Agents', desc: isEs ? 'Equipo de IA 24/7 para restaurantes.' : '24/7 AI workforce for restaurants.', href: '/ai-agents-for-restaurants' },
        { icon: 'Workflow', label: isEs ? 'Automatización IA Personalizada' : 'Custom AI Automation', desc: isEs ? 'Flujos a medida que conectan tus sistemas.' : 'Tailored workflows connecting your systems.', href: '/ai/custom-ai-automation' },
      ],
    },
    {
      title: isEs ? 'Productos de IA' : 'AI Products',
      href: '/ai-solutions',
      items: [
        { icon: 'Phone', label: isEs ? 'Recepcionista IA' : 'AI Receptionist', desc: isEs ? 'Llamadas, reservas y soporte al cliente.' : 'Calls, reservations & guest support.', href: '/ai/ai-receptionist' },
        { icon: 'Star', label: isEs ? 'Automatización de Reseñas' : 'Review Automation', desc: isEs ? 'Responde reseñas automáticamente.' : 'Respond automatically to reviews.', href: '/ai/review-automation' },
        { icon: 'Utensils', label: 'Catering AI', desc: isEs ? 'En lanzamiento final · La plataforma completa de catering.' : 'Being finalized · The complete AI catering platform.', href: '/ai/catering-ai', comingSoon: true },
        { icon: 'Users', label: isEs ? 'Conoce al Equipo IA' : 'Meet the AI Team', desc: isEs ? 'Penny, Sonny, Rachel y compañía.' : 'Penny, Sonny, Rachel & co.', href: '/ai-team' },
      ],
    },
  ];
}

/* -------------------------------------------------------------------- */
/*  Mega-menu component                                                  */
/* -------------------------------------------------------------------- */

function MegaMenu({ groups, onClose, cta, columns = 4 }) {
  const gridCls = columns === 2 ? 'grid-cols-2' : columns === 3 ? 'grid-cols-3' : 'grid-cols-4';
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className="mt-2 w-full bg-white border border-neutral-200/80 rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] overflow-hidden"
      onMouseLeave={onClose}
      data-testid="mega-menu"
    >
      <div className={`grid ${gridCls} gap-px bg-neutral-100`}>
        {groups.map((g) => (
          <div key={g.title} className="bg-white p-6">
            {g.href ? (
              <Link
                to={g.href}
                onClick={onClose}
                className="group/cat inline-flex items-center gap-1 text-[11px] font-semibold tracking-[0.14em] uppercase text-neutral-500 hover:text-[#F77F11] mb-4 transition-colors"
                data-testid={`mega-group-link-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {g.title}
                <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover/cat:opacity-100 group-hover/cat:translate-x-0 transition-all" />
              </Link>
            ) : (
              <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-neutral-500 mb-4">
                {g.title}
              </p>
            )}
            <ul className="space-y-1">
              {g.items.map((it) => {
                const I = Icons[it.icon] || Icons.Sparkles;
                return (
                  <li key={it.label}>
                    <Link
                      to={it.href}
                      onClick={onClose}
                      className="group flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-orange-50/70 transition-colors"
                      data-testid={`mega-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                    >
                      <span className="w-8 h-8 shrink-0 rounded-md bg-orange-50 group-hover:bg-[#F77F11] flex items-center justify-center transition-colors">
                        <I className="w-4 h-4 text-[#F77F11] group-hover:text-white transition-colors" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-neutral-900 leading-tight">
                          {it.label}
                          {it.comingSoon && (
                            <span className="px-1.5 py-0.5 rounded-full bg-orange-50 text-[#F77F11] text-[9px] tracking-wide font-semibold uppercase">
                              Soon
                            </span>
                          )}
                        </span>
                        <span className="block text-[11.5px] text-neutral-500 leading-snug mt-0.5">
                          {it.desc}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {cta && (
        <div className="bg-gradient-to-r from-orange-50 via-orange-50/70 to-white px-6 py-4 flex items-center justify-between border-t border-orange-100/70">
          <p className="text-sm text-neutral-700">{cta.label}</p>
          <Link
            to={cta.href}
            onClick={onClose}
            className="inline-flex items-center gap-1 text-[#F77F11] hover:text-[#d8651a] font-semibold text-sm"
            data-testid="mega-cta"
          >
            {cta.action} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </motion.div>
  );
}

/* -------------------------------------------------------------------- */
/*  Auth slot — discreet Login link, or AI OS admin menu when signed in  */
/* -------------------------------------------------------------------- */

function AuthSlot({ user, isEs }) {
  const [open, setOpen] = useState(false);
  if (user === undefined) return null;
  if (!user) {
    return (
      <Link to="/login" className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] xl:text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="nav-login">
        <Lock className="w-3.5 h-3.5" /> Login
      </Link>
    );
  }
  const items = [
    { label: 'Kitxens AI OS', href: '/admin/ai-os' },
    { label: isEs ? 'Editorial Inbox' : 'Editorial Inbox', href: '/admin/editor' },
    { label: 'Affiliates', href: '/admin/affiliates' },
  ];
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] xl:text-sm font-semibold text-neutral-900" data-testid="nav-admin-menu">
        <ShieldCheck className="w-4 h-4 text-emerald-500" /> AI OS
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-3 w-56 z-50">
          <div className="rounded-xl border border-neutral-200 bg-white shadow-xl p-1.5">
            {items.map((it) => (
              <Link key={it.href} to={it.href} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 text-[13.5px] font-medium text-neutral-700 hover:bg-neutral-50 hover:text-[#F77F11] transition-colors" data-testid={`admin-menu-${it.href.split('/').pop()}`}>
                {it.label}
              </Link>
            ))}
            <div className="my-1 border-t border-neutral-100" />
            <button
              onClick={async () => { await logoutSession(); clearAuthCache(); window.location.href = '/'; }}
              className="w-full text-left rounded-lg px-3 py-2 text-[13.5px] font-medium text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-2"
              data-testid="admin-menu-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> {isEs ? 'Cerrar sesión' : 'Log out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Header                                                              */
/* -------------------------------------------------------------------- */

export default function Header() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const { lang, setLang } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null); // 'services' | 'ai' | null
  const [mobileGroup, setMobileGroup] = useState(null);
  const { pathname } = useLocation();
  const authUser = useAuthUser();

  const serviceGroups = buildServiceGroups(lang);
  const aiGroups = buildAiGroups(lang);
  const isEs = lang === 'es';

  // Top-level nav (left of right-side CTAs)
  const navItems = [
    { key: 'services', label: isEs ? 'Servicios' : 'Services', mega: 'services', href: '/services' },
    { key: 'ai', label: isEs ? 'Soluciones IA' : 'AI Solutions', mega: 'ai', href: '/ai-solutions' },
    { key: 'how', label: isEs ? 'Cómo Funciona' : 'How It Works', href: '/how-it-works' },
    { key: 'pricing', label: isEs ? 'Precios' : 'Pricing', href: '/pricing' },
    { key: 'company', label: isEs ? 'Empresa' : 'Company', href: '/aboutus' },
    { key: 'magazine', label: isEs ? 'Recursos' : 'Resources', href: '/magazine' },
    { key: 'faq', label: 'FAQ', href: '/faq' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveMenu(null);
  }, [pathname]);

  useEffect(() => {
    document.body.classList.toggle('kx-menu-open', open);
    return () => document.body.classList.remove('kx-menu-open');
  }, [open]);

  const linkCls = (active) =>
    `whitespace-nowrap text-[13.5px] xl:text-[15px] font-medium transition-colors ${active ? 'text-neutral-900' : 'text-neutral-600 hover:text-neutral-900'}`;

  const isAuthPage = pathname === '/login' || pathname === '/account' ||
    pathname.startsWith('/affiliates/dashboard') || pathname.startsWith('/admin');

  return (
    <header
      className={`kx-nozoom fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isAuthPage
          ? 'bg-white shadow-[0_2px_20px_-10px_rgba(0,0,0,0.12)] border-b border-neutral-100'
          : scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-[0_2px_20px_-10px_rgba(0,0,0,0.1)] border-b border-neutral-100'
          : 'bg-white/70 backdrop-blur-sm'
      }`}
      onMouseLeave={() => setActiveMenu(null)}
      data-testid="site-header"
    >
      <motion.div
        aria-hidden="true"
        style={{ scaleX }}
        className="absolute bottom-0 left-0 right-0 h-[2.5px] origin-left bg-gradient-to-r from-[#F77F11] via-[#ff9b4a] to-[#F77F11]"
        data-testid="scroll-progress"
      />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-7 xl:px-10 h-16 flex items-center justify-between gap-4 xl:gap-6 relative">
        <Logo />

        <nav className="hidden lg:flex items-center gap-3 xl:gap-7">
          {navItems.map((l) => {
            if (l.mega) {
              const isOpen = activeMenu === l.mega;
              return (
                <Link
                  key={l.key}
                  to={l.href}
                  onMouseEnter={() => setActiveMenu(l.mega)}
                  onClick={() => setActiveMenu(null)}
                  className={`${linkCls(pathname === l.href)} inline-flex items-center gap-1`}
                  data-testid={`nav-${l.key}`}
                >
                  {l.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </Link>
              );
            }
            if (l.external) {
              return (
                <a key={l.key} href={l.href} target="_blank" rel="noopener noreferrer" className={linkCls(false)} data-testid={`nav-${l.key}`}>
                  {l.label}
                </a>
              );
            }
            const isActive = pathname === l.href;
            return (
              <Link key={l.key} to={l.href} className={linkCls(isActive)} data-testid={`nav-${l.key}`}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2 xl:gap-3">
          <div className="text-xs font-medium text-neutral-500 flex items-center gap-1">
            <button
              onClick={() => setLang('en')}
              className={`px-1.5 ${lang === 'en' ? 'text-neutral-900 font-semibold' : 'hover:text-neutral-700'}`}
              data-testid="lang-toggle-en-desktop"
            >
              EN
            </button>
            <span className="opacity-40">/</span>
            <button
              onClick={() => setLang('es')}
              className={`px-1.5 ${lang === 'es' ? 'text-neutral-900 font-semibold' : 'hover:text-neutral-700'}`}
              data-testid="lang-toggle-es-desktop"
            >
              ES
            </button>
          </div>
          <Link to="/contact" className="whitespace-nowrap text-[13px] xl:text-sm font-medium text-neutral-700 hover:text-neutral-900" data-testid="nav-contact">
            {isEs ? 'Contacto' : 'Contact'}
          </Link>
          <AuthSlot user={authUser} isEs={isEs} />
          <Button
            asChild
            className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-10 px-5 rounded-full text-sm font-semibold shadow-sm hover:shadow-md transition-all"
          >
            <Link to="/#audit" data-testid="nav-free-audit" className="group/audit">
              <span className="inline-grid text-center">
                <span className="col-start-1 row-start-1 transition-all duration-200 group-hover/audit:opacity-0 group-hover/audit:-translate-y-1.5">
                  {isEs ? 'Auditoría Gratis' : 'Free Audit'}
                </span>
                <span className="col-start-1 row-start-1 opacity-0 translate-y-1.5 transition-all duration-200 group-hover/audit:opacity-100 group-hover/audit:translate-y-0 font-bold tracking-wide">
                  {isEs ? '¡HAZLO YA!' : 'DO IT NOW'}
                </span>
              </span>
              <ArrowRight className="w-4 h-4 ml-1 transition-transform duration-200 group-hover/audit:translate-x-1" />
            </Link>
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2.5">
          <div className="text-xs font-medium text-neutral-500 flex items-center gap-1">
            <button
              onClick={() => setLang('en')}
              className={`px-1 ${lang === 'en' ? 'text-neutral-900 font-semibold' : ''}`}
              data-testid="lang-toggle-en-mobile"
            >
              EN
            </button>
            <span className="opacity-40">/</span>
            <button
              onClick={() => setLang('es')}
              className={`px-1 ${lang === 'es' ? 'text-neutral-900 font-semibold' : ''}`}
              data-testid="lang-toggle-es-mobile"
            >
              ES
            </button>
          </div>
          <button className="text-neutral-800 p-1" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* LIVE RESTAURANT INTELLIGENCE — between the menu and the page content (hidden on auth pages) */}
      {!isAuthPage && <IntelligenceBar />}

      {/* Mega menu container */}
      <AnimatePresence>
        {activeMenu && (
          <div
            className="hidden lg:block absolute left-0 right-0 top-full px-4"
            onMouseEnter={() => setActiveMenu(activeMenu)}
          >
            <div className="max-w-[1240px] mx-auto">
              {activeMenu === 'services' ? (
                <MegaMenu
                  groups={serviceGroups}
                  columns={4}
                  onClose={() => setActiveMenu(null)}
                  cta={{
                    label: isEs ? 'Explora todos los servicios de tecnología y crecimiento.' : 'Explore all technology and growth services.',
                    action: isEs ? 'Ver todos los servicios' : 'See all services',
                    href: '/services',
                  }}
                />
              ) : (
                <MegaMenu
                  groups={aiGroups}
                  columns={2}
                  onClose={() => setActiveMenu(null)}
                  cta={{
                    label: isEs ? '¿Listo para activar IA en tu restaurante?' : 'Ready to activate AI in your restaurant?',
                    action: isEs ? 'Solicita una consultoría IA gratis' : 'Request a Free AI Consultation',
                    href: '/contact',
                  }}
                />
              )}
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Mobile drawer — slides in from the right (portal: escapes header transform) */}
      {createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 top-16 z-[90] bg-black/35 backdrop-blur-[2px]"
              onClick={() => setOpen(false)}
              data-testid="mobile-drawer-backdrop"
            />
            <motion.div
              key="mobile-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden fixed right-0 top-16 bottom-0 z-[95] w-[86%] max-w-sm bg-white shadow-2xl overflow-y-auto overscroll-contain"
              data-testid="mobile-drawer"
            >
            <div className="px-6 pt-4 pb-12 flex flex-col gap-1" style={{ paddingBottom: 'calc(3rem + env(safe-area-inset-bottom))' }}>
              <div className="flex items-center justify-between py-2 border-b border-neutral-100 mb-1">
                <span className="text-[12px] font-semibold text-neutral-500">{isEs ? 'Tamaño del texto' : 'Text size'}</span>
                <TextSizeInline />
              </div>
              <MobileGroupAccordion label={isEs ? 'Servicios' : 'Services'} href="/services" open={mobileGroup === 'services'} onToggle={() => setMobileGroup((g) => (g === 'services' ? null : 'services'))}>
                {serviceGroups.map((g) => (
                  <MobileGroupBlock key={g.title} title={g.title} href={g.href} items={g.items} />
                ))}
              </MobileGroupAccordion>

              <MobileGroupAccordion label={isEs ? 'Soluciones IA' : 'AI Solutions'} href="/ai-solutions" open={mobileGroup === 'ai'} onToggle={() => setMobileGroup((g) => (g === 'ai' ? null : 'ai'))}>
                {aiGroups.map((g) => (
                  <MobileGroupBlock key={g.title} title={g.title} href={g.href} items={g.items} />
                ))}
              </MobileGroupAccordion>

              {navItems
                .filter((l) => !l.mega)
                .map((l) =>
                  l.external ? (
                    <a
                      key={l.key}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[15px] text-neutral-800 font-medium py-2.5 border-b border-neutral-100"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      key={l.key}
                      to={l.href}
                      onClick={() => setOpen(false)}
                      className="text-[15px] text-neutral-800 font-medium py-2.5 border-b border-neutral-100"
                    >
                      {l.label}
                    </Link>
                  ),
                )}
              <Link to="/contact" onClick={() => setOpen(false)} className="text-[15px] text-neutral-800 font-medium py-2.5 border-b border-neutral-100">
                {isEs ? 'Contacto' : 'Contact'}
              </Link>
              <Link to="/affiliates" onClick={() => setOpen(false)} className="text-[15px] font-semibold py-2.5 border-b border-neutral-100 inline-flex items-center gap-2 text-[#F77F11]" data-testid="mobile-nav-affiliates">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F77F11] animate-pulse" />
                {isEs ? 'Partners — Gana 30%' : 'Partners — Earn 30%'}
              </Link>
              <Link to={authUser ? '/admin/mission-control' : '/login'} onClick={() => setOpen(false)} className="text-[15px] text-neutral-800 font-medium py-2.5 border-b border-neutral-100 inline-flex items-center gap-2" data-testid="mobile-nav-login">
                {authUser ? <><ShieldCheck className="w-4 h-4 text-emerald-500" /> Mission Control</> : <><Lock className="w-4 h-4" /> Login</>}
              </Link>
              <Link
                to="/#audit"
                onClick={() => setOpen(false)}
                className="group/audit mt-3 inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 px-5 rounded-full text-sm font-semibold"
              >
                <span className="inline-grid text-center">
                  <span className="col-start-1 row-start-1 transition-all duration-200 group-hover/audit:opacity-0 group-hover/audit:-translate-y-1.5">
                    {isEs ? 'Auditoría Gratis' : 'Free Audit'}
                  </span>
                  <span className="col-start-1 row-start-1 opacity-0 translate-y-1.5 transition-all duration-200 group-hover/audit:opacity-100 group-hover/audit:translate-y-0 font-bold tracking-wide">
                    {isEs ? '¡HAZLO YA!' : 'DO IT NOW'}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
}

/* -------------------------------------------------------------------- */
/*  Mobile helpers                                                       */
/* -------------------------------------------------------------------- */

function MobileGroupAccordion({ label, href, open, onToggle, children }) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-neutral-100">
        {href ? (
          <Link to={href} className="flex-1 text-[15px] text-neutral-800 font-medium py-2.5" data-testid={`mobile-nav-link-${href.replace(/\//g, '')}`}>
            {label}
          </Link>
        ) : (
          <span className="flex-1 text-[15px] text-neutral-800 font-medium py-2.5">{label}</span>
        )}
        <button onClick={onToggle} className="p-2.5 -mr-2" aria-label={`Toggle ${label}`} data-testid={`mobile-nav-toggle-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && <div className="pb-2">{children}</div>}
    </>
  );
}

function MobileGroupBlock({ title, href, items }) {
  return (
    <div className="pl-2 py-2">
      {href ? (
        <Link
          to={href}
          className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-500 hover:text-[#F77F11] mb-2"
          data-testid={`mobile-group-link-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          {title}
          <ArrowRight className="w-3 h-3" />
        </Link>
      ) : (
        <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-500 mb-2">{title}</p>
      )}
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.label}>
            <Link to={it.href} className="block text-[14px] text-neutral-700 py-1 hover:text-[#F77F11]">
              {it.label}
              {it.comingSoon && <span className="ml-1.5 text-[10px] text-[#F77F11]">· Soon</span>}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
